import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { MbsPopupType, ModalComponent, SharedPersistentStateEnum, TableHeader } from 'mbs-ui-kit';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { AgentType, ComputersFiltersType, GetComputersParams } from 'mbs-ui-shared@models/Computer';
import {
  AgentAvailableBuildVersions,
  AgentCommand,
  ComputerAvailableVersions,
  ComputerBasedModal
} from 'mbs-ui-shared@models/ComputersModals';
import { BuildVersionType } from 'mbs-ui-shared@models/build';
import { GroupActionMode, ParamsForceUpdate, UpdateBuild } from 'mbs-ui-shared@models/rm/remote-command-model';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { RmCommandsAbstractWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';
import { Observable, forkJoin, noop, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ForceUpdateAgentType } from './computers-force-update-modal-interface';

const subHeaders: TableHeader[] = [
  {
    name: '',
    class: '-stretch pl-0',
    gridColSize: '1fr'
  }
];
const headers: TableHeader[] = [
  {
    name: '',
    class: '-stretch pl-0',
    gridColSize: '1fr',
    gridColMin: '150px'
  },
  {
    name: '',
    gridColSize: '250px',
    gridColMin: '250px',
    class: '-stretch text-end'
  }
];

@UntilDestroy()
@Component({
  selector: 'mbs-computers-force-update-detail-modal',
  template: `
    <mbs-modal [isCustomModal]="true" title="{{ title }}" #modal>
      <div modal-body>
        <ng-container *ngIf="data.resultList?.length; else loader">
          <h5 *ngIf="data.isGroupAction">{{ subTitle }}</h5>
          <mbs-table-grid
            [multipleSelect]="false"
            classesTable="-accordion"
            rowClasses="background-light"
            childHeaderClasses="background-white"
            bindParentKey="hid"
            bindChildren="childs"
            [collapsibleMode]="true"
            [viewMode]="sharedPersistentStateEnum.table"
            [stripedTable]="false"
            [showNav]="false"
            [showHeaders]="false"
            [headers]="headers"
            [minHeight]="'200px'"
            [height]="'calc(100vh - 8rem - 139px)'"
            [showChildrenHeaders]="false"
            [subtitleHeaders]="subHeaders"
            [data]="data.resultList">
            <ng-template mbsTableCell let-error>
              <div class="row align-items-center">
                <div class="col-auto text-center">
                  <span class="mbs-table-grid_ico text-primary">
                    <i class="fa fa-desktop text-primary"></i>
                  </span>
                </div>
                <div class="col">
                  <div class="mbs-table-grid_accordion-title">
                    {{ error.name || error.displayName }}
                  </div>
                </div>
              </div>
            </ng-template>
            <ng-template mbsTableCell let-error>
              <div class="row justify-content-end">
                <div class="col-auto lh-1" *ngFor="let child of error.childs">
                  <mbs-status
                    [icon]="child.ok ? 'ico ico-check-circle' : 'ico ico-exclamation-circle'"
                    [type]="child.ok ? 'success' : 'danger'">
                    {{ child.agentType }}
                  </mbs-status>
                </div>
              </div>
            </ng-template>

            <ng-template mbsTableCell group="child" let-child>
              <div class="force-update-details">
                <div class="fw-semibold mb-1">{{ child.agentType }}</div>
                <div class="form-row">
                  <div class="col-auto lh-1">
                    <span class="mbs-status">
                      <span class="mbs-status_ico -sm fa fa-circle text-{{ child.ok ? 'success' : 'danger' }} mr-0"></span>
                    </span>
                  </div>
                  <div class="col">
                    {{ extractMessage(child.message) }}
                  </div>
                </div>
              </div>
            </ng-template>
          </mbs-table-grid>
        </ng-container>

        <ng-template #loader>
          <div style="height: calc(100vh - 8rem - 139px);"><mbs-loader></mbs-loader></div>
        </ng-template>
      </div>
      <ng-container modalFooter>
        <mbs-button type="primary" (click)="modal.close()">
          {{ 'shared:buttons.ok' | i18next: { format: 'title' } }}
        </mbs-button>
        <mbs-button type="secondary" data-dismiss="modal" (click)="modal.close()">
          {{ 'shared:buttons.close' | i18next: { format: 'title' } }}
        </mbs-button>
      </ng-container>
    </mbs-modal>
  `
})
export class ComputersForceUpdateDetailModalComponent {
  public title: string;
  public data;
  // headers for table in modal with error
  public headers: TableHeader[] = headers;
  // subheaders for table in modal with error
  public subHeaders: TableHeader[] = subHeaders;
  public get subTitle(): string {
    const compText = this.i18nPipe.transform('shared:computers.computer', { format: 'title', count: this.data.resultList.lengt || 0 });

    return `${compText}: ${this.data.resultList.length}/${this.data.totalComputers}`;
  }

  protected readonly mbsPopupType = MbsPopupType;
  protected readonly sharedPersistentStateEnum = SharedPersistentStateEnum;

  constructor(private i18nPipe: I18NextPipe) {}

  extractMessage(original: string): string {
    let result = original;

    if (original.includes('ErrorMessage')) {
      try {
        const JSONString = `{${original.split('{')[1].split('}')[0]}}`;

        result = JSON.parse(JSONString)?.ErrorMessage || original;
      } catch (e) {
        noop();
      }
    }

    return result;
  }
}

@UntilDestroy()
@Component({
  selector: 'mbs-computers-force-update-modal',
  templateUrl: './computers-force-update-modal.component.html',
  styleUrls: ['./computers-force-update-modal.component.scss']
})
export class ComputersForceUpdateModalComponent implements ComputerBasedModal, OnInit {
  public readonly forceUpdateText = 'Force Update';
  public readonly sharedPersistentStateEnum = SharedPersistentStateEnum;
  public readonly alertType = MbsPopupType;
  // about computer
  public computer: ComputerAvailableVersions = null;
  public computerName = '';
  // about group computers
  public isSandBoxAvailable = false;
  public hids: string[] = [];
  // TODO: Remove it after removing legacy Computers page
  public legacyFilters = {
    searchQuery: null,
    unSupportedVersion: false,
    companyId: null,
    hidden: false
  };
  public filters: Observable<GetComputersParams>;
  // support Builds
  public backupAgent: AgentAvailableBuildVersions = null;
  public rmmAgent: AgentAvailableBuildVersions = null;
  public raAgent: AgentAvailableBuildVersions = null;
  public availableBuilds: AgentAvailableBuildVersions[];
  public isAvailableBackup = false;
  public isAvailableRMM = false;
  public isAvailableRA = false;
  // if using group action
  public mode = GroupActionMode.Single;
  // loading state
  public isLoading = true;
  public loading = false;
  // copy enum for using in view (HTML)
  public readonly forceUpdateAgentType = ForceUpdateAgentType;
  public readonly buildVersionType = BuildVersionType;
  public readonly agentType = AgentType;

  // headers for table in modal with error
  public headers: TableHeader[] = headers;
  // subheaders for table in modal with error
  public subHeaders: TableHeader[] = subHeaders;

  @ViewChild(ModalComponent, { static: true }) private baseModal;

  public forceUpdateForm = new UntypedFormGroup(
    {
      backup: new FormControl(ForceUpdateAgentType.NotNecessary),
      rmm: new FormControl(ForceUpdateAgentType.NotNecessary),
      ra: new FormControl(ForceUpdateAgentType.NotNecessary)
    },
    [this.buildsValidator.bind(this)]
  );

  public get isGroupAction(): boolean {
    return [GroupActionMode.Group, GroupActionMode.Bulk].includes(this.mode);
  }

  constructor(
    private rmCommands: RmCommandsAbstractWrapper,
    private facade: ComputersFacade,
    private buildService: BuildService,
    public i18nextService: I18NextService,
    public i18nPipe: I18NextPipe
  ) {}

  ngOnInit(): void {
    if (!this.isGroupAction) {
      forkJoin([this.buildService.isSandBoxAvailable(), this.facade.getAvailableVersions(this.computer.hid)]).subscribe(
        ([isSandBoxAvailable, computer]) => {
          this.isSandBoxAvailable = isSandBoxAvailable;
          this.computer = cloneDeep(computer);
          this.computerName =
            this.computer.displayName && this.computer.displayName !== '' ? this.computer.displayName : this.computer.name;

          this.computer.applications.forEach((agent: AgentAvailableBuildVersions) => {
            switch (agent.applicationId.toLowerCase()) {
              case AgentType.Backup:
                this.backupAgent = agent;
                return;
              case AgentType.RMM:
                this.rmmAgent = agent;
                return;
              case AgentType.RA:
                this.raAgent = agent;
                return;
            }
          });
          this.availableBuilds = [this.backupAgent, this.rmmAgent, this.raAgent].filter(
            (agent: AgentAvailableBuildVersions) => !(agent === null || agent === undefined)
          );
          this.isAvailableBackup = this.isAvailableVersion(this.backupAgent, false) || this.isAvailableVersion(this.backupAgent, true);
          this.isAvailableRMM = this.isAvailableVersion(this.rmmAgent, false) || this.isAvailableVersion(this.rmmAgent, true);
          this.isAvailableRA = this.isAvailableVersion(this.raAgent, false);
          this.updateForm();
          this.isLoading = false;
        }
      );
    } else {
      this.buildService.isSandBoxAvailable().subscribe((isSandBoxAvailable: boolean) => {
        this.isSandBoxAvailable = isSandBoxAvailable;
        this.updateForm();
        this.isLoading = false;
      });
    }
  }

  /*
   * Check is Agent force update supporting and online
   * @param agent
   */
  isForceUpdateSupporting(agent: AgentAvailableBuildVersions): boolean {
    return agent && agent.online && agent.forceUpdateSupporting;
  }

  /*
   * Updating form for one computer
   * @private
   */
  private updateForm(): void {
    this.forceUpdateForm.patchValue({
      backup: this.setDefaultValueByType(AgentType.Backup),
      rmm: this.setDefaultValueByType(AgentType.RMM),
      ra: this.setDefaultValueByType(AgentType.RA)
    });
  }

  /*
   * Return default value to FormControl by available version build
   * @param agentType
   * @private
   */

  private setDefaultValueByType(agentType): number | null {
    if (this.isGroupAction) {
      return ForceUpdateAgentType.NotNecessary;
    } else {
      if (
        this.isOneAvailableBuildOnly() ||
        (this.backupAgent && !this.rmmAgent && !this.raAgent) ||
        (this.rmmAgent && !this.backupAgent && !this.raAgent)
      ) {
        switch (agentType) {
          case AgentType.Backup:
            if (this.backupAgent === null || this.backupAgent === undefined) return null;
            return this.isAvailableVersion(this.backupAgent, false) ? ForceUpdateAgentType.Public : ForceUpdateAgentType.Sandbox;
          case AgentType.RMM:
            if (this.rmmAgent === null || this.rmmAgent === undefined) return null;
            return this.isAvailableVersion(this.rmmAgent, false) ? ForceUpdateAgentType.Public : ForceUpdateAgentType.Sandbox;
          case AgentType.RA:
            if (this.raAgent === null || this.raAgent === undefined) return null;
            return this.isAvailableVersion(this.raAgent, false) ? ForceUpdateAgentType.Public : null;
          default:
            return null;
        }
      } else {
        return this.isAgentTypeByApplicationID(agentType) ? ForceUpdateAgentType.NotNecessary : null;
      }
    }
  }

  /*
   * Send payload/payloadBulk by type command
   */
  handleResolve(): void {
    // TODO STATEFUL STREAMS
    let inited = false;

    this.loading = true;

    const fullData = of(this.mode).pipe(
      switchMap((mode) => {
        if (inited) {
          return of(false);
        }

        const prepareUpdate = this.prepareUpdateBuilds();

        inited = true;

        // TODO: Remove legacy block after removing legacy Computers page
        if (mode === GroupActionMode.Legacy) {
          return this.rmCommands.bulkForceUpdate(
            {
              search: this.legacyFilters.searchQuery,
              companyIds: [this.legacyFilters.companyId],
              loadHidden: this.legacyFilters.hidden,
              type: this.legacyFilters.unSupportedVersion ? ComputersFiltersType.Unsupported : ComputersFiltersType.None,
              appIds: prepareUpdate.map((app) => app.appId as AgentType)
            },
            prepareUpdate
          );
        }
        if (mode === GroupActionMode.Bulk) {
          return this.filters.pipe(
            take(1),
            map((filters) => ({ ...filters, appIds: prepareUpdate.map((app) => app.appId as AgentType) })),
            switchMap((filters) => this.rmCommands.bulkForceUpdate(filters, prepareUpdate))
          );
        }
        return this.rmCommands.groupForceUpdate(
          this.prepareAgentCommands(mode === GroupActionMode.Group ? this.hids : [this.computer.hid])
        );
      }),
      catchError(() => {
        return of(false);
      })
    );

    this.facade
      .handleGroupOperation(fullData, {
        operationText: this.forceUpdateText,
        isGroupAction: this.isGroupAction,
        detailModal: ComputersForceUpdateDetailModalComponent
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.baseModal.close();
          this.loading = false;
        }
      });
  }

  /*
   * Validation for the presence of at least one agent
   * @param form
   */
  buildsValidator(form: UntypedFormGroup): ValidationErrors | null {
    const backupControl = form.get('backup').value;
    const rmmControl = form.get('rmm').value;
    const raControl = form.get('ra').value;

    if (backupControl || rmmControl || raControl) {
      return null;
    }

    return { agent: { message: 'Not any build available version for updating' } };
  }

  /*
   * Checking for the presence of an agent in the computer apps by id
   * @param type
   */
  isAgentTypeByApplicationID(type: string): boolean {
    return this.computer.applications.some((app) => app.applicationId.toLowerCase() === type.toLowerCase());
  }

  /*
   * Check is available version for build
   * @param agent
   * @param isSandbox
   */
  isAvailableVersion(agent: AgentAvailableBuildVersions, isSandbox: boolean): boolean {
    if (agent === null || agent === undefined) return false;
    if (isSandbox && !this.isSandBoxAvailable) return false;
    const buildVersionType = agent.builds[isSandbox ? BuildVersionType.Sandbox : BuildVersionType.Public];

    return (
      !(agent.builds === null || agent.builds === undefined) &&
      !(buildVersionType === null || buildVersionType === undefined) &&
      !(buildVersionType.version === null || buildVersionType.version === undefined) &&
      buildVersionType.version !== '' &&
      versionCompare(buildVersionType.version, agent.currentVersion) > 0
    );
  }

  /*
   * Helper method for wrapping text brackets (using in view)
   * @param text
   */
  wrapTextBranch(text): string {
    return text ? '(' + text + ')' : '';
  }

  /*
   * Check the computer has more than one application
   */
  isPluralAgentBuilds(): boolean {
    return this.availableBuilds.length > 1;
  }

  /*
   * Check the computer has online and supporting only one build for updating
   */
  isOneAvailableBuildOnly(): boolean {
    if (this.availableBuilds.length === 1) {
      const isForceUpdateSupporting = this.isForceUpdateSupporting(this.availableBuilds[0]);
      const isAvailablePublicVersion = this.isAvailableVersion(this.availableBuilds[0], false) && isForceUpdateSupporting;
      const isAvailableSandboxVersion = this.isAvailableVersion(this.availableBuilds[0], true) && isForceUpdateSupporting;

      return (isAvailablePublicVersion && !isAvailableSandboxVersion) || (!isAvailablePublicVersion && isAvailableSandboxVersion);
    }
    return false;
  }

  /*
   * Check the computer has online and supporting any one (one or more) build for updating
   */
  isAvailableAndSupportedAnyOneBuild(): boolean {
    return (
      (this.isAvailableBackup && this.isForceUpdateSupporting(this.backupAgent)) ||
      (this.isAvailableRMM && this.isForceUpdateSupporting(this.rmmAgent)) ||
      (this.isAvailableRA && this.isForceUpdateSupporting(this.raAgent))
    );
  }

  /*
   * Prepare agentCommands before PUT on server
   * @private
   */
  private prepareAgentCommands(hids: string[]): AgentCommand<ParamsForceUpdate>[] {
    const form = this.forceUpdateForm.value;
    const updateBuilds = this.prepareUpdateBuilds();
    const agentTypes = Object.entries(form)
      .filter(([, value]) => Number(value) > 0)
      .map(([key]) => key as AgentType);

    return hids.flatMap((hid) =>
      agentTypes.map((agentType) => ({
        computerHid: hid,
        agentType,
        params: {
          updateBuilds
        }
      }))
    );
  }

  /*
   * Prepare updateBuilds before PUT on server
   * @private
   */
  private prepareUpdateBuilds(): UpdateBuild[] {
    const form = this.forceUpdateForm.value;
    return Object.entries(form)
      .filter(([, value]) => Number(value) > 0)
      .map(([key, value]: [string, number]) => {
        return {
          appId: key,
          versionType: Object.keys(ForceUpdateAgentType)[Object.values(ForceUpdateAgentType).indexOf(value)]
        };
      });
  }

  /*
   * Get classes for insert to ico in mbs-status
   * @param agent
   */
  getStatusClasses(agent: AgentAvailableBuildVersions): string {
    if (!agent.online) {
      return 'ico ico-pause-circle text-muted';
    } else if (agent.online && !agent.forceUpdateSupporting) {
      return 'ico ico-exclamation-circle text-danger';
    } else {
      return 'ico ico-check-circle text-success';
    }
  }
}
